import React, { useCallback, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import JSZip from 'jszip';
import { useSnackbar } from 'notistack';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import FlowImportFlow from 'Shared/flows/flows-interface/FlowImportFlow';
import { convertFileName, exportFlows, notFoundMessage, simplifyJson } from 'Utils/flowExport';
import { copyText } from 'Utils/clipboard';

const uniqueFileName = (name, used) => {
    const fileName = convertFileName(name);
    if (!used.has(fileName)) {
        used.add(fileName);
        return fileName;
    }

    const base = fileName.replace(/\.json$/, '');
    let index = 2;
    while (used.has(`${base}_${index}.json`)) {
        index += 1;
    }
    used.add(`${base}_${index}.json`);
    return `${base}_${index}.json`;
};

const archiveName = (count) => {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}-${pad(now.getMinutes())}`;
    return `infiniflow-export_${date}_${time}_${count}-flows.zip`;
};

const toSimpleObjects = (exported) => exported.map((flow) => JSON.parse(simplifyJson(JSON.stringify(flow))));

const FlowBulkExport = ({ visible, setVisible, flows, onRemove, onExported }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [busy, setBusy] = useState(false);
    const [viewer, setViewer] = useState(null);
    const [includeSubflows, setIncludeSubflows] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const { flows: exported, not_found: notFound } = await exportFlows(flows.map((flow) => flow.id), includeSubflows);

            if (notFound.length) {
                enqueueSnackbar(notFoundMessage(notFound), {
                    action: CustomSnackbar,
                    variant: 'warning',
                });
            }

            return exported;
        } catch (err) {
            enqueueSnackbar(`Unable to export: ${err.response?.data?.message ?? err.message}`, {
                action: CustomSnackbar,
                variant: 'error',
            });

            return [];
        }
    }, [flows, includeSubflows, enqueueSnackbar]);

    const handleOpen = async (flow) => {
        setBusy(true);
        try {
            const { flows: [exported] } = await exportFlows([flow.id], false);
            setViewer({ name: flow.name, flowId: flow.id, response: JSON.stringify(exported), simpleVersion: true });
        } catch {
            enqueueSnackbar(`Unable to export: ${flow.name}`, {
                action: CustomSnackbar,
                variant: 'error',
            });
        } finally {
            setBusy(false);
        }
    };

    const handleShow = async () => {
        setBusy(true);
        try {
            const exported = await fetchAll();
            if (!exported.length) {
                return;
            }
            setViewer({
                name: 'infiniflow-export',
                flowId: null,
                response: JSON.stringify(toSimpleObjects(exported)),
                simpleVersion: false,
            });
        } finally {
            setBusy(false);
        }
    };

    const handleCopy = async () => {
        setBusy(true);
        try {
            const exported = await fetchAll();
            if (!exported.length) {
                return;
            }

            await copyText(JSON.stringify(toSimpleObjects(exported), null, 4));
            enqueueSnackbar(`${exported.length} flows have been copied to the clipboard.`, {
                action: CustomSnackbar,
                variant: 'success',
            });
        } catch {
            enqueueSnackbar('Failed to copy to clipboard', {
                action: CustomSnackbar,
                variant: 'error',
            });
        } finally {
            setBusy(false);
        }
    };

    const handleDownload = async () => {
        setBusy(true);
        try {
            const exported = await fetchAll();
            if (!exported.length) {
                return;
            }

            const zip = new JSZip();
            const used = new Set();
            toSimpleObjects(exported).forEach((flow) => {
                zip.file(uniqueFileName(flow.name, used), JSON.stringify(flow, null, 4));
            });

            const blob = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = archiveName(exported.length);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            onExported();
            setVisible(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Dialog
                open={visible}
                onClose={() => setVisible(false)}
                TransitionComponent={Transition}
                sx={{
                    '& .MuiDialog-paper': {
                        width: '960px',
                        maxWidth: '100%',
                        maxHeight: '90vh',
                        margin: '16px',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '8px',
                    }}
                >
                    <Box component="span" sx={{ color: '#3B465C', fontWeight: 600 }}>
                        Export Flows ({flows.length})
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeSubflows}
                                    onChange={(e) => setIncludeSubflows(e.target.checked)}
                                    size="small"
                                    disabled={busy}
                                />
                            }
                            label="Include subflows"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '14px',
                                    color: '#3B465C',
                                },
                            }}
                        />
                        {busy && <CircularProgress size={20} />}
                        <Button onClick={handleShow} variant="outlined" size="small" disabled={busy || !flows.length}>
                            Show
                        </Button>
                        <Button onClick={handleDownload} variant="outlined" size="small" disabled={busy || !flows.length}>
                            Download
                        </Button>
                        <Button onClick={handleCopy} variant="outlined" size="small" disabled={busy || !flows.length}>
                            Copy
                        </Button>
                        <Button onClick={() => setVisible(false)} variant="outlined" size="small" disabled={busy}>
                            Close
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {!flows.length && (
                        <Typography sx={{ color: '#D76A6A', fontWeight: 500, textAlign: 'center', padding: '20px' }}>
                            No flows selected
                        </Typography>
                    )}
                    {flows.map((flow) => (
                        <Box
                            key={flow.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                marginBottom: '8px',
                                padding: '8px 16px',
                                backgroundColor: '#F2F5F8',
                                borderRadius: '8px',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500, minWidth: '40px', flexShrink: 0 }}
                            >
                                {flow.id}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{ flex: 1, minWidth: 0, color: '#3B465C', overflowWrap: 'anywhere', cursor: 'pointer' }}
                                onClick={() => handleOpen(flow)}
                            >
                                {flow.name}
                            </Typography>
                            <Tooltip title={
                                <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Remove from the export</span>
                            } arrow>
                                <IconButton onClick={() => onRemove(flow.id)} disabled={busy}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>
            {viewer && (
                <FlowImportFlow
                    name={viewer.name}
                    title="Export Flow"
                    visible={true}
                    setVisible={() => setViewer(null)}
                    response={viewer.response}
                    setResponse={() => {}}
                    status={null}
                    flowId={viewer.flowId}
                    simpleVersion={viewer.simpleVersion}
                />
            )}
        </>
    );
};

export default FlowBulkExport;
